import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";
import { useCart, priceLabel, hasVariants } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { ArrowRight, Pencil, X, FileText, Search, Sun, Moon, Clock } from "lucide-react";
import ShipMap from "@/components/ShipMap";
import { CANNABINOID_INFO } from "@/lib/cannabinoids";

const STAFF_ROLES = ["admin", "owner", "manager", "employee"];
const CANNABINOID_OPTIONS = ["CBD", "CBN", "THC", "CBC", "CBG"];

function CannabinoidReport({ code, onClose, variant }) {
  const info = CANNABINOID_INFO[code];
  if (!info) return null;
  return (
    <div data-testid="cannabinoid-report" className={`border border-copper/40 bg-charcoal ${variant === "fixed" ? "shadow-2xl shadow-black/60" : ""}`}>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div data-testid="cannabinoid-report-name" className="font-serif text-2xl text-copper leading-none">{info.name}</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-sand/55 mt-1.5">{info.full}</div>
          </div>
          <button data-testid="cannabinoid-report-close" aria-label="Close" onClick={onClose} className="text-sand/60 hover:text-copper flex-shrink-0"><X size={18} /></button>
        </div>
        <span className="inline-block mt-3 border border-copper/50 text-copper px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em]">{info.tag}</span>
        <p className="mt-3 text-sm text-sand/85 leading-relaxed">{info.desc}</p>
        <Link to="/learn#cannabinoids" data-testid="cannabinoid-report-learn" className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-copper hover:text-[#E5C158]">
          Read more on Learn <ArrowRight size={13} />
        </Link>
      </div>
    </div>
  );
}

const PLANT_STYLES = {
  indica: "bg-[#5B3A6E] text-white",
  sativa: "bg-[#4E7A34] text-white",
  hybrid: "bg-copper text-navy",
};

const TIME_META = {
  daytime: { icon: Sun, label: "Daytime" },
  nighttime: { icon: Moon, label: "Nighttime" },
  anytime: { icon: Clock, label: "Anytime" },
};

function ScanBadges({ product }) {
  const plant = (product.plant_type || "").trim();
  const time = (product.time_of_day || "").trim();
  const timeMeta = TIME_META[time.toLowerCase()];
  const TimeIcon = timeMeta?.icon;
  if (!plant && !timeMeta) return null;
  return (
    <div data-testid={`scan-badges-${product.slug}`} className="mt-3 flex flex-wrap items-center gap-2">
      {plant && (
        <span
          data-testid={`badge-plant-${product.slug}`}
          className={`px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] ${PLANT_STYLES[plant.toLowerCase()] || "bg-navy text-white"}`}
        >
          {plant}
        </span>
      )}
      {timeMeta && (
        <span
          data-testid={`badge-time-${product.slug}`}
          className="inline-flex items-center gap-1.5 border border-copper/50 text-charcoal px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em]"
        >
          {TimeIcon && <TimeIcon size={13} className="text-copper" />}
          {timeMeta.label}
        </span>
      )}
    </div>
  );
}

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [params, setParams] = useSearchParams();
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState(params.get("q") || "");
  const [cannabinoid, setCannabinoid] = useState("");
  const { addItem } = useCart();
  const { user } = useAuth();
  const isStaff = STAFF_ROLES.includes(user?.role);

  const activeCat = params.get("category") || "all";
  const activeSub = params.get("sub") || "";
  const activeBrand = params.get("brand") || "";
  const activeBrandName = brands.find((b) => b.slug === activeBrand)?.name || "";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const slug = activeSub || (activeCat !== "all" ? activeCat : "");
    const q = {};
    if (slug) q.category = slug;
    if (activeBrand) q.brand = activeBrand;
    if (search.trim()) q.q = search.trim();
    if (cannabinoid) q.cannabinoid = cannabinoid;
    const t = setTimeout(() => {
      api.get("/products", { params: q }).then((r) => setProducts(r.data));
    }, 250);
    return () => clearTimeout(t);
  }, [activeCat, activeSub, activeBrand, search, cannabinoid]);

  const topCats = useMemo(() => categories.filter((c) => !c.parent_id), [categories]);
  const subCats = useMemo(() => {
    const parent = topCats.find((c) => c.slug === activeCat);
    return parent ? categories.filter((c) => c.parent_id === parent.id) : [];
  }, [categories, topCats, activeCat]);
  const activeParentName = topCats.find((c) => c.slug === activeCat)?.name || "";
  const allTabs = useMemo(() => [{ slug: "all", name: "All Products" }, ...topCats], [topCats]);

  const selectTab = (slug) => setParams(slug === "all" ? {} : { category: slug });
  const selectSub = (slug) => setParams(slug ? { category: activeCat, sub: slug } : { category: activeCat });

  return (
    <div data-testid="shop-page">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 lg:pt-16 pb-2">
        <span className="eyebrow">The Shop</span>
        <h1 className="font-serif text-4xl sm:text-5xl text-sand mt-3 leading-tight">Shop our catalog.</h1>
        <p className="mt-3 text-sm sm:text-base text-sand/70 max-w-2xl font-light leading-relaxed">
          Every product is third-party lab tested. Search, filter, and add to cart — check out online or reserve for local pickup.
        </p>
        {activeBrand && (
          <div data-testid="shop-brand-filter" className="mt-5 inline-flex items-center gap-3 border border-copper/40 bg-charcoal px-4 py-2">
            <span className="text-[11px] uppercase tracking-[0.2em] text-sand/60">Brand</span>
            <span className="font-serif text-lg text-copper leading-none">{activeBrandName || activeBrand}</span>
            <button
              data-testid="shop-brand-clear"
              onClick={() => setParams({})}
              className="text-sand/60 hover:text-copper"
              aria-label="Clear brand filter"
            >
              <X size={16} />
            </button>
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-16">
        {/* Search */}
        <div className="relative mb-8 max-w-xl">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal/40" />
          <input
            data-testid="shop-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, type (Indica, Sativa, Hybrid), or time (Daytime, Nighttime)…"
            className="w-full bg-sand border border-copper/30 focus:border-copper focus:outline-none text-navy placeholder:text-charcoal/45 pl-11 pr-10 py-3 text-sm"
          />
          {search && (
            <button
              data-testid="shop-search-clear"
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal/50 hover:text-navy"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Cannabinoid filter */}
        <div data-testid="cannabinoid-filter" className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-sand/60 mb-3">Filter by cannabinoid</div>
          <div className="flex flex-wrap gap-2.5">
            <button
              data-testid="cannabinoid-all"
              onClick={() => setCannabinoid("")}
              className={`px-4 py-1.5 text-xs uppercase tracking-[0.18em] border transition-colors ${
                !cannabinoid ? "border-copper bg-copper text-navy" : "border-copper/40 text-sand/80 hover:border-copper"
              }`}
            >
              All
            </button>
            {CANNABINOID_OPTIONS.map((c) => (
              <button
                key={c}
                data-testid={`cannabinoid-${c}`}
                onClick={() => setCannabinoid((prev) => (prev === c ? "" : c))}
                className={`px-4 py-1.5 text-xs uppercase tracking-[0.18em] border transition-colors ${
                  cannabinoid === c ? "border-copper bg-copper text-navy" : "border-copper/40 text-sand/80 hover:border-copper"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {/* Inline cannabinoid report (small screens — never covers the grid) */}
          {cannabinoid && (
            <div className="lg:hidden mt-4 max-w-md" data-testid="cannabinoid-report-inline">
              <CannabinoidReport code={cannabinoid} onClose={() => setCannabinoid("")} variant="inline" />
            </div>
          )}
        </div>

        {/* Main categories */}
        <div className="flex flex-wrap gap-x-8 gap-y-3 border-b border-copper/25 pb-1 -mx-2">
          {allTabs.map((c) => (
            <button
              key={c.slug}
              data-testid={`filter-${c.slug}`}
              onClick={() => selectTab(c.slug)}
              className={`relative px-2 py-3 text-xs sm:text-sm uppercase tracking-[0.2em] transition-colors ${
                activeCat === c.slug
                  ? "text-copper after:absolute after:inset-x-2 after:-bottom-0.5 after:h-0.5 after:bg-copper"
                  : "text-sand/70 hover:text-copper"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {/* Sub-categories (only when the active main category has children) */}
        {subCats.length > 0 && (
          <div data-testid="subcat-bar" className="flex flex-wrap gap-2.5 mt-6">
            <button
              data-testid="subfilter-all"
              onClick={() => selectSub("")}
              className={`px-4 py-1.5 text-xs uppercase tracking-[0.18em] border transition-colors ${
                !activeSub ? "border-copper bg-copper text-navy" : "border-copper/40 text-sand/80 hover:border-copper"
              }`}
            >
              All {activeParentName}
            </button>
            {subCats.map((s) => (
              <button
                key={s.slug}
                data-testid={`subfilter-${s.slug}`}
                onClick={() => selectSub(s.slug)}
                className={`px-4 py-1.5 text-xs uppercase tracking-[0.18em] border transition-colors ${
                  activeSub === s.slug ? "border-copper bg-copper text-navy" : "border-copper/40 text-sand/80 hover:border-copper"
                }`}
              >
                {s.name}
              </button>
            ))}
          </div>
        )}

        {products.length === 0 ? (
          <div className="py-24 text-center text-sand/70">{search.trim() || cannabinoid ? "No products match your filters." : "No products in this category yet."}</div>
        ) : (
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {products.map((p) => (
              <div key={p.id} data-testid={`product-card-${p.slug}`} className="group float-card bg-sand border border-copper/20 overflow-hidden hover:border-copper transition-all flex flex-col">
                <Link
                  to={`/shop/${p.slug}`}
                  data-testid={`product-link-${p.slug}`}
                  className="relative aspect-[4/5] overflow-hidden bg-white block w-full"
                >
                  {p.image_url && (
                    <img src={mediaUrl(p.image_url)} alt={p.name} className="w-full h-full object-contain p-3 group-hover:scale-[1.03] transition-transform duration-700" />
                  )}
                  {p.is_new && (
                    <span className="absolute top-3 left-3 bg-copper text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em]">New</span>
                  )}
                </Link>
                <div className="pt-5 px-5 pb-5 flex flex-col flex-1">
                  <Link to={`/shop/${p.slug}`} className="text-left font-serif text-2xl text-navy leading-tight hover:text-copper transition-colors">{p.name}</Link>
                  <ScanBadges product={p} />
                  <p className="text-sm text-charcoal/70 mt-2 leading-relaxed line-clamp-2">{p.short_description}</p>
                  {p.cannabinoids?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {p.cannabinoids.map((c) => (
                        <span key={c} className="border border-copper text-copper px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em]">{c}</span>
                      ))}
                    </div>
                  )}
                  {p.effects?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {p.effects.slice(0, 3).map((e) => (
                        <span key={e} className="border border-tan/60 text-charcoal/85 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em]">{e}</span>
                      ))}
                    </div>
                  )}
                  <div className="mt-4 flex items-center justify-between border-t border-tan/30 pt-3">
                    <span className="text-sm text-copper">{priceLabel(p)}</span>
                    <button type="button" data-testid={`quickview-${p.slug}`} onClick={() => setSelected(p)} className="text-xs uppercase tracking-[0.2em] text-charcoal/60 hover:text-navy">Quick View →</button>
                  </div>
                  {p.batch_number && (
                    <Link
                      to={`/lab-reports?batch=${encodeURIComponent(p.batch_number)}`}
                      data-testid={`view-coa-${p.slug}`}
                      className="mt-3 inline-flex items-center justify-center gap-1.5 text-xs uppercase tracking-[0.2em] text-copper hover:text-navy border border-copper/40 hover:border-copper py-2 transition-colors"
                    >
                      <FileText size={13} /> View COA
                    </Link>
                  )}
                  <button
                    data-testid={`add-to-cart-${p.slug}`}
                    onClick={() => {
                      if (hasVariants(p)) { setSelected(p); return; }
                      addItem(p, 1); toast.success(`${p.name} added to cart`);
                    }}
                    className="mt-3 w-full bg-navy text-white py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-charcoal transition-colors"
                  >
                    {hasVariants(p) ? "Choose Options" : "Add to Cart"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Shipping map + local CTA — at the very bottom of the shop */}
      <section className="border-t border-copper/20 bg-black">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-14 pb-20">
          <div className="text-center mb-8">
            <span className="eyebrow">We Ship Nationwide</span>
            <h2 className="font-serif text-3xl sm:text-4xl text-sand mt-3 leading-tight">From Texarkana to your doorstep.</h2>
          </div>
          <ShipMap />
          <div data-testid="shop-local-cta" className="mt-12 text-center">
            <h2 className="font-serif text-3xl sm:text-4xl text-sand leading-tight max-w-3xl mx-auto">
              Don&apos;t have anywhere to shop locally near you?
            </h2>
            <p className="mt-3 font-serif italic text-3xl sm:text-4xl text-copper">We got you covered!</p>
            <button
              type="button"
              data-testid="shop-local-cta-btn"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="mt-8 inline-flex items-center gap-2 bg-copper text-navy px-10 py-4 text-sm uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C158] transition-colors"
            >
              Start Shopping <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Desktop: cannabinoid report pops up on the right side — doesn't cover the grid */}
      {cannabinoid && (
        <div className="hidden lg:block fixed right-5 top-28 w-80 z-30 animate-fade-up" data-testid="cannabinoid-report-side">
          <CannabinoidReport code={cannabinoid} onClose={() => setCannabinoid("")} variant="fixed" />
        </div>
      )}

      <QuickView product={selected} open={!!selected} onClose={() => setSelected(null)} isStaff={isStaff} />
    </div>
  );
}

function QuickView({ product, open, onClose, isStaff }) {
  const { addItem } = useCart();
  const [variant, setVariant] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    if (product) {
      const vs = product.variants || [];
      setVariant(vs.find((v) => v.in_stock !== false) || vs[0] || null);
      setImgIdx(0);
    }
  }, [product]);

  if (!product) return null;
  const images = [product.image_url, ...(product.gallery || [])].filter(Boolean);
  const cover = images[imgIdx] || images[0] || "";
  const varList = product.variants || [];
  const hasVar = varList.length > 0;
  const soldOut = hasVar && (!variant || variant.in_stock === false);
  const price = hasVar
    ? (variant ? `$${(Number(variant.price) || 0).toFixed(2)}` : "Select an option")
    : (product.price_display || "Contact for pricing");

  const doAdd = () => {
    if (soldOut) { toast.error("Please choose an available option."); return; }
    addItem(product, 1, hasVar ? variant : null);
    toast.success(`${product.name} added to cart`);
    onClose();
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent data-testid="quickview-panel" className="w-full sm:max-w-md md:max-w-lg overflow-y-auto bg-navy text-sand border-copper/30 [&>button]:hidden">
        <button data-testid="quickview-close" onClick={onClose} className="absolute right-4 top-4 text-sand/70 hover:text-copper z-10"><X size={20} /></button>
        <div className="pt-2">
          <div className="aspect-[4/5] bg-white overflow-hidden">
            {cover && <img src={mediaUrl(cover)} alt={product.name} className="w-full h-full object-contain p-3" />}
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {images.map((src, i) => (
                <button key={i} data-testid={`quickview-thumb-${i}`} onClick={() => setImgIdx(i)} className={`w-14 h-14 bg-white overflow-hidden border-2 ${i === imgIdx ? "border-copper" : "border-transparent"}`}>
                  <img src={mediaUrl(src)} alt="" className="w-full h-full object-contain p-1" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-5">
            <div className="flex items-start justify-between gap-3">
              <SheetTitle data-testid="quickview-name" className="font-serif text-3xl text-sand leading-tight text-left">{product.name}</SheetTitle>
              {product.is_new && <span className="bg-copper text-navy px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] self-center">New</span>}
            </div>
            <p className="mt-3 text-sand/80 leading-relaxed font-light">{product.short_description}</p>
            <div data-testid="quickview-price" className="mt-4 font-serif text-2xl text-copper">{price}</div>
            {soldOut && <div className="mt-1 text-sm text-texasred uppercase tracking-[0.15em]">Currently sold out</div>}

            {hasVar && (
              <div className="mt-5">
                <div className="text-[11px] uppercase tracking-[0.22em] text-sand/70 mb-3">Choose an option</div>
                <div className="flex flex-wrap gap-2.5">
                  {varList.map((v) => {
                    const oos = v.in_stock === false;
                    const active = variant?.id === v.id;
                    return (
                      <button
                        key={v.id}
                        data-testid={`quickview-variant-${v.id}`}
                        disabled={oos}
                        onClick={() => setVariant(v)}
                        className={`px-4 py-2 text-sm border transition-colors ${oos ? "border-sand/20 text-sand/30 line-through cursor-not-allowed" : active ? "border-copper bg-copper text-navy" : "border-sand/30 text-sand/85 hover:border-copper"}`}
                      >
                        {v.label} · ${(Number(v.price) || 0).toFixed(2)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {product.effects?.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-1.5">
                {product.effects.map((e) => (
                  <span key={e} className="border border-sand/25 text-sand/85 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em]">{e}</span>
                ))}
              </div>
            )}

            <button
              data-testid="quickview-add"
              onClick={doAdd}
              className="mt-6 w-full bg-copper text-navy py-3 text-sm uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C158] transition-colors"
            >
              Add to Cart
            </button>

            <div className="mt-4 flex items-center justify-between">
              <Link to={`/shop/${product.slug}`} data-testid="quickview-details" onClick={onClose} className="inline-flex items-center gap-1.5 text-sm uppercase tracking-[0.18em] text-sand/80 hover:text-copper">
                View full details <ArrowRight size={14} />
              </Link>
              {isStaff && (
                <Link to={`/admin/products?edit=${product.slug}`} data-testid="quickview-edit" className="inline-flex items-center gap-1.5 text-sm uppercase tracking-[0.18em] text-copper hover:text-[#E5C158]">
                  <Pencil size={13} /> Edit
                </Link>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
